import { sitApi } from './sitApiClient';
import { callQualityJudge } from '../../../shared/services/qualityJudgeService';

/**
 * Convert a File (from <FileInput> / drop) to base64 (no data: prefix).
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * POST /imports — one OR MORE diagrams (tree) → SET of SIT test plans.
 *
 * `diagrams`: array of { id, label, file, parentDiagramId?, parentBranch? }.
 * Exactly one entry must have no parentDiagramId (the root). Every other entry
 * must reference an existing parent and supply a parentBranch hint
 * (e.g. "press 1", "default transfer").
 *
 * Returns: { plans: TestPlan[], modelId, retries, warnings: string[] }.
 */
export async function importDiagrams({
  diagrams,
  notes,
  maxPlans,
  ivrStyle,
  expectPromptBaseTimeoutSec,
  priorJudgeFindings
}) {
  const encoded = await Promise.all(
    diagrams.map(async (d) => ({
      id: d.id,
      label: d.label,
      imageBase64: await fileToBase64(d.file),
      mediaType: d.file.type || 'image/png',
      parentDiagramId: d.parentDiagramId ?? null,
      parentBranch: d.parentBranch ?? null
    }))
  );
  const body = { diagrams: encoded };
  if (notes) body.notes = notes;
  if (maxPlans) body.maxPlans = maxPlans;
  if (ivrStyle && ivrStyle !== 'hybrid') body.ivrStyle = ivrStyle;
  if (typeof expectPromptBaseTimeoutSec === 'number' && expectPromptBaseTimeoutSec !== 5) {
    body.expectPromptBaseTimeoutSec = expectPromptBaseTimeoutSec;
  }
  if (Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0) {
    body.priorJudgeFindings = priorJudgeFindings;
  }
  const { data } = await sitApi.post('/imports', body);
  return data;
}

/**
 * Legacy single-diagram wrapper for callers that haven't migrated.
 * Prefer importDiagrams() for new code.
 */
export async function importDiagram(file, notes, maxPlans) {
  return importDiagrams({
    diagrams: [{ id: 'root', label: 'Root diagram', file }],
    notes,
    maxPlans
  });
}

/**
 * POST /imports kind=kb — KB artifacts → conversational AI-agent test plans.
 *
 * Each entry in `artifacts` is { id, label, file?, text? }:
 *   - file (.pdf): base64-encoded, sent as `kind: "pdf"` to Bedrock.
 *   - text (md/html/plain): sent inline as `kind: "text"`.
 *
 * `inlineText` is an additional engineer-pasted KB body (Markdown / plain
 * text). Either artifacts OR inlineText is required.
 *
 * `testMix` controls how many of each kind of test to emit:
 *   { positivePerTopic?: 1, includeNegative?: true, includePersona?: true }
 *
 * Returns: { plans: TestPlan[], modelId, retries, warnings, kbArtifacts }.
 */
export async function importKb({
  artifacts,
  inlineText,
  notes,
  maxPlans,
  testMix,
  targetDefaults,
  expectPromptBaseTimeoutSec,
  priorJudgeFindings
}) {
  const encoded = await Promise.all(
    (artifacts || []).map(async (a) => {
      if (a.file) {
        const isPdf = (a.file.type || '').includes('pdf');
        return {
          id: a.id,
          label: a.label,
          kind: isPdf ? 'pdf' : 'text',
          body: isPdf ? await fileToBase64(a.file) : await fileToText(a.file),
          mediaType: a.file.type || (isPdf ? 'application/pdf' : 'text/plain')
        };
      }
      return {
        id: a.id,
        label: a.label,
        kind: 'text',
        body: a.text,
        mediaType: a.mediaType || 'text/markdown'
      };
    })
  );
  const body = { kind: 'kb', artifacts: encoded };
  if (inlineText) body.inlineText = inlineText;
  if (notes) body.notes = notes;
  if (maxPlans) body.maxPlans = maxPlans;
  if (testMix) body.testMix = testMix;
  if (targetDefaults) body.targetDefaults = targetDefaults;
  if (typeof expectPromptBaseTimeoutSec === 'number' && expectPromptBaseTimeoutSec !== 5) {
    body.expectPromptBaseTimeoutSec = expectPromptBaseTimeoutSec;
  }
  if (Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0) {
    body.priorJudgeFindings = priorJudgeFindings;
  }
  const { data } = await sitApi.post('/imports', body);
  return data;
}

// File → UTF-8 text (for .md / .html / .txt KB artifacts).
function fileToText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
}

/**
 * POST /imports — Excel test-script spreadsheet → SET of SIT test plans.
 *
 * Backend reuses the existing `/imports` handler with a `kind: "excel"`
 * discriminator: deterministic client-side parsing groups rows into test
 * cases, then Bedrock (Sonnet 4.6) synthesizes each group's free-form
 * `callerExperience` text into typed steps (expectPrompt / pressDtmf /
 * saySpeech / expectTransfer / hangup). Response shape matches
 * importDiagrams().
 *
 * `groups`: [{ testNo, section?, expectedResults?, preconditions?,
 *              rows: [{ sequence, callerExperience, agentExperience? }] }]
 * `targetDefaults`: { ccaas?, destination?, fromDid? } — applied to every
 *   synthesized plan's `target` block.
 * `source`: { filename?, sheetName? } — preserved for warnings/debugging.
 *
 * Returns: { plans: TestPlan[], modelId, retries?, warnings?: string[] }.
 *
 * Dual-mode. The backend may respond either way:
 *   - 200 OK  + { plans, ... }         → legacy sync path (ASYNC_IMPORTS_ENABLED=false
 *                                        on the Lambda, OR body carries sync:true).
 *                                        Return immediately — no polling.
 *   - 202 Accepted + { importId, ... } → async path (ASYNC_IMPORTS_ENABLED=true).
 *                                        Poll GET /imports/{importId} until terminal.
 *
 * This dual handling lets the Lambda and SPA be deployed in any order and
 * the feature flag flipped per env without coupling. The whole chunk-split
 * loop that used to live here is gone — server-side chunking in
 * runChunkedExcelImport handles that now when async mode is active. See
 * docs/sit-async-imports.md for the full design.
 */
const POLL_INITIAL_MS = 1500;
const POLL_MAX_MS = 10000;
const POLL_BACKOFF = 1.3;
const POLL_HARD_LIMIT_MS = 10 * 60 * 1000;

export async function importExcel(
  {
    groups,
    targetDefaults,
    source,
    notes,
    maxPlans,
    matcherPreference,
    expectPromptBaseTimeoutSec,
    priorJudgeFindings
  },
  { onChunkProgress } = {}
) {
  const targetCount = Math.min(
    typeof maxPlans === 'number' ? maxPlans : groups.length,
    groups.length
  );
  const usable = groups.slice(0, targetCount);

  const body = {
    kind: 'excel',
    groups: usable,
    targetDefaults: targetDefaults ?? {},
    source: source ?? {}
  };
  if (notes) body.notes = notes;
  if (maxPlans) body.maxPlans = maxPlans;
  if (matcherPreference && matcherPreference !== 'auto') body.matcherPreference = matcherPreference;
  if (typeof expectPromptBaseTimeoutSec === 'number' && expectPromptBaseTimeoutSec !== 5) {
    body.expectPromptBaseTimeoutSec = expectPromptBaseTimeoutSec;
  }
  if (Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0) {
    body.priorJudgeFindings = priorJudgeFindings;
  }

  // One POST for the whole sheet. The backend decides whether to run it
  // synchronously (returns 200 + plans) or asynchronously (returns 202 +
  // importId). We detect from the response shape, not the status code —
  // axios surfaces status via response.status but 2xx all land as data.
  const resp = await sitApi.post('/imports', body);
  if (resp.status === 202 || (resp.data && resp.data.importId && resp.data.status === 'PENDING')) {
    return await pollUntilDone(resp.data.importId, usable.length, onChunkProgress);
  }

  // Legacy sync response — single tick for the progress UI then return.
  const data = resp.data;
  onChunkProgress?.({
    chunk: 1,
    totalChunks: 1,
    plansSoFar: data.plans?.length ?? 0,
    totalPlans: usable.length
  });
  return data;
}

async function pollUntilDone(importId, totalPlansEstimate, onChunkProgress) {
  const start = Date.now();
  let delay = POLL_INITIAL_MS;
  // Tick the UI once immediately so the user sees "starting…" instead of
  // staring at a frozen spinner for the first 1.5s.
  onChunkProgress?.({ chunk: 0, totalChunks: 1, plansSoFar: 0, totalPlans: totalPlansEstimate });

  while (true) {
    await sleep(delay);
    const { data: state } = await sitApi.get(`/imports/${encodeURIComponent(importId)}`);

    if (state.progress) {
      onChunkProgress?.({
        chunk: state.progress.chunksDone,
        totalChunks: state.progress.chunksTotal,
        plansSoFar: state.progress.plansSoFar,
        totalPlans: totalPlansEstimate
      });
    }

    if (state.status === 'COMPLETED') {
      // result shape mirrors the old sync 200 body: { plans, modelId, retries, warnings }
      return state.result ?? { plans: [], modelId: '', retries: 0, warnings: [] };
    }
    if (state.status === 'FAILED') {
      const err = new Error(state.error?.message || 'Import failed');
      // Shape-compat with axios errors so the existing banner handlers
      // (`err?.response?.data?.message || err.message`) still work.
      err.response = { data: state.error ?? {}, status: 422 };
      throw err;
    }
    if (state.status === 'CANCELLED') {
      const err = new Error('Import was cancelled');
      err.response = { data: { code: 'IMPORT_CANCELLED', message: 'Import was cancelled' }, status: 409 };
      throw err;
    }

    delay = Math.min(delay * POLL_BACKOFF, POLL_MAX_MS);
    if (Date.now() - start > POLL_HARD_LIMIT_MS) {
      const err = new Error(`Import did not complete within ${POLL_HARD_LIMIT_MS / 1000}s; still ${state.status}`);
      err.response = { data: { code: 'IMPORT_POLL_TIMEOUT' }, status: 504 };
      throw err;
    }
  }
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Async, advisory quality grade for a set of plans.
 *
 * Fired by every importer in parallel right after `/imports` returns, so the
 * result panel can render plans immediately and update the quality badge
 * 15-30s later when the judge finishes. Hits the direct `/quality-judge` route
 * (no importer Lambda hop, so the 30s API GW timeout has more headroom for
 * Sonnet's response).
 *
 * sourceContent caveat for diagrams: the SPA only has the file labels, parent
 * branches, and engineer notes — not the image bytes. The judge can still
 * grade structural concerns (terminality, matcher-type fit, brand-hallucination,
 * obvious completeness gaps) but deep faithfulness ("does the plan match the
 * actual menu choices on screen?") is out of reach without re-uploading the
 * image. Documented in source.description so the judge knows what it's
 * working with.
 *
 * Returns: { overallScore: number, perItem: [{itemId, score, severity, reason,
 *           suggestion}], modelId, durationMs }
 */
export async function judgeImport({ kind, source, sourceContent, plans }) {
  return callQualityJudge({ kind, source, sourceContent, plans });
}
